import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { usePollWhileVisible } from '../../hooks/usePollWhileVisible';
import { notificationService } from '../../services/notificationService';
import { isPublicPath } from '../navModel';
import { haptic } from '../haptics';
import { openLayer } from '../backStack';
import { ChromeProvider, ToastInput } from './ChromeContext';
import MobileTopBar from './MobileTopBar';
import MoreSheet from './MoreSheet';
import NotificationsSheet from './NotificationsSheet';

/**
 * The phone app frame (≤ 768px, signed in, not a customer link page): top bar, «المزيد»
 * sheet, notifications sheet and toasts. The bottom tab bar is BottomNav.
 *
 * While it is up, `body.m-shell` hides the per-page desktop Navbar (each page still renders
 * one — the frame replaces it on phones instead of 40 pages changing).
 */
const OPEN_MORE = 'mize:open-more';

export const openMoreSheet = () => window.dispatchEvent(new Event(OPEN_MORE));

const Toaster: React.FC = () => {
  const [items, setItems] = useState<Array<ToastInput & { id: number }>>([]);
  useEffect(() => {
    let seq = 0;
    const onToast = (e: Event) => {
      const input = (e as CustomEvent<ToastInput>).detail;
      const id = ++seq;
      setItems((cur) => [...cur.slice(-2), { ...input, id }]);
      window.setTimeout(() => setItems((cur) => cur.filter((x) => x.id !== id)), input.ms || 2800);
    };
    window.addEventListener('m-toast', onToast);
    return () => window.removeEventListener('m-toast', onToast);
  }, []);
  if (!items.length) return null;
  return (
    <div className="m-toasts" role="status" aria-live="polite">
      {items.map((it) => (
        <div key={it.id} className={`m-toast${it.tone && it.tone !== 'default' ? ` tone-${it.tone}` : ''}`}>
          <span className="m-toast-msg">{it.message}</span>
          {it.action && (
            <button
              type="button"
              className="m-toast-action"
              onClick={() => {
                it.action!.onClick();
                setItems((cur) => cur.filter((x) => x.id !== it.id));
              }}
            >
              {it.action.label}
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

/**
 * Phone scroll behaviour the app never had: a new screen opens at the top, and «رجوع» returns
 * to where the list was. Positions are remembered per history entry while scrolling, then
 * restored on a back/forward once the page is tall enough (lists load after the route renders).
 */
function useScrollMemory() {
  const location = useLocation();
  const navType = useNavigationType();
  const positions = useRef(new Map<string, number>());
  const keyRef = useRef(location.key);
  keyRef.current = location.key;

  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        positions.current.set(keyRef.current, window.scrollY);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const shownKey = useRef(location.key);
  useLayoutEffect(() => {
    // A Back that only closed a sheet or the chat lands on the same page entry: leave the scroll alone.
    if (shownKey.current === location.key) return;
    shownKey.current = location.key;
    if (navType !== 'POP') {
      window.scrollTo(0, 0);
      return;
    }
    const target = positions.current.get(location.key) || 0;
    if (!target) return;
    let tries = 0;
    let raf = 0;
    const attempt = () => {
      const room = document.documentElement.scrollHeight - window.innerHeight;
      if (room >= target || tries++ > 60) {
        window.scrollTo(0, Math.min(target, Math.max(0, room)));
        return;
      }
      raf = requestAnimationFrame(attempt);
    };
    attempt();
    return () => cancelAnimationFrame(raf);
  }, [location.key, navType]);
}

/**
 * The app's existing dialogs (≈30 of them, `.modal-content` with a `.modal-header`) are bottom
 * sheets on phones already; this lets them be swiped down from their header like every other
 * sheet. Closing goes through the dialog's own close button, so its own cleanup still runs —
 * nothing is closed that the person could not have closed with a tap.
 */
function useLegacyDialogSwipe() {
  useEffect(() => {
    let el: HTMLElement | null = null;
    let y0 = 0;
    let dy = 0;
    let t0 = 0;
    const closeButton = (content: HTMLElement) =>
      content.querySelector<HTMLElement>('.modal-header .modal-close, .modal-header .modal-close-icon, .modal-close, .modal-close-icon');
    const start = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const t = e.target as HTMLElement;
      if (t.closest('button, a, input, select, textarea')) return;
      const content = t.closest<HTMLElement>('.modal-content');
      if (!content || !t.closest('.modal-header') || !closeButton(content)) return;
      el = content;
      y0 = e.touches[0].clientY;
      dy = 0;
      t0 = Date.now();
    };
    const move = (e: TouchEvent) => {
      if (!el) return;
      dy = Math.max(0, e.touches[0].clientY - y0);
      if (dy < 4) return;
      e.preventDefault();
      el.style.transition = 'none';
      el.style.transform = `translate3d(0, ${dy}px, 0)`;
    };
    const end = () => {
      if (!el) return;
      const content = el;
      el = null;
      const fast = dy > 30 && dy / Math.max(1, Date.now() - t0) > 0.6;
      content.style.transition = 'transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1)';
      const btn = closeButton(content);
      if (btn && (dy > content.offsetHeight * 0.25 || fast)) {
        haptic.tick();
        content.style.transform = 'translate3d(0, 100%, 0)';
        window.setTimeout(() => btn.click(), 160);
      } else {
        content.style.transform = '';
      }
    };
    document.addEventListener('touchstart', start, { passive: true });
    document.addEventListener('touchmove', move, { passive: false });
    document.addEventListener('touchend', end);
    document.addEventListener('touchcancel', end);
    return () => {
      document.removeEventListener('touchstart', start);
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', end);
      document.removeEventListener('touchcancel', end);
    };
  }, []);
}

/**
 * Back closes the app's existing dialogs too. Each `.modal-overlay` that appears gets one history
 * layer (see backStack); Back presses the dialog's own close button, so its cleanup runs exactly
 * as if it had been tapped. A dialog that goes away by itself (saved, cancelled) gives the entry
 * back. A dialog with no close button just stays — Back then leaves the page, as before.
 */
function useLegacyDialogBack() {
  useEffect(() => {
    const layers = new Map<Element, () => void>();
    const closeButton = (overlay: Element) =>
      overlay.querySelector<HTMLElement>('.modal-header .modal-close, .modal-close, .modal-close-icon');
    let frame = 0;
    const sync = () => {
      frame = 0;
      const now = new Set(Array.from(document.querySelectorAll('.modal-overlay')));
      layers.forEach((dismiss, el) => {
        if (!now.has(el)) {
          layers.delete(el);
          dismiss();
        }
      });
      now.forEach((el) => {
        if (layers.has(el)) return;
        layers.set(el, openLayer(() => {
          layers.delete(el);
          closeButton(el)?.click();
        }));
      });
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(sync);
    };
    const obs = new MutationObserver(schedule);
    obs.observe(document.body, { childList: true, subtree: true });
    schedule();
    return () => {
      obs.disconnect();
      if (frame) cancelAnimationFrame(frame);
      layers.forEach((dismiss) => dismiss());
      layers.clear();
    };
  }, []);
}

/**
 * While the keyboard is up (a text box has focus), `html.m-kb` hides the tab bar and the order
 * dock — on Android they would otherwise ride on top of the keyboard and eat a third of what is
 * left of the screen. Focus is the signal (not the viewport size): it is exact on every phone.
 */
function useKeyboardClass() {
  useEffect(() => {
    const html = document.documentElement;
    let tid = 0;
    const NOT_TEXT = ['checkbox', 'radio', 'file', 'range', 'button', 'submit', 'reset', 'color', 'image', 'hidden'];
    const isTextEntry = (el: Element | null) => {
      if (!el) return false;
      if ((el as HTMLElement).isContentEditable || el.tagName === 'TEXTAREA') return true;
      return el.tagName === 'INPUT' && !NOT_TEXT.includes(((el as HTMLInputElement).type || 'text').toLowerCase());
    };
    const onIn = (e: FocusEvent) => {
      if (!isTextEntry(e.target as Element)) return;
      window.clearTimeout(tid);
      html.classList.add('m-kb');
    };
    // Focus hopping between two boxes passes through "nothing focused" — wait a beat.
    const onOut = () => {
      window.clearTimeout(tid);
      tid = window.setTimeout(() => {
        if (!isTextEntry(document.activeElement)) html.classList.remove('m-kb');
      }, 120);
    };
    document.addEventListener('focusin', onIn);
    document.addEventListener('focusout', onOut);
    return () => {
      document.removeEventListener('focusin', onIn);
      document.removeEventListener('focusout', onOut);
      window.clearTimeout(tid);
      html.classList.remove('m-kb');
    };
  }, []);
}

const Frame: React.FC = () => {
  const [moreOpen, setMoreOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const { pathname } = useLocation();

  const refreshUnread = useCallback(async () => {
    try {
      setUnread(await notificationService.getUnreadCount());
    } catch {
      /* keep the last count */
    }
  }, []);
  usePollWhileVisible(refreshUnread, 30_000, [refreshUnread]);

  useEffect(() => {
    const open = () => setMoreOpen(true);
    window.addEventListener(OPEN_MORE, open);
    document.body.classList.add('m-shell');
    return () => {
      window.removeEventListener(OPEN_MORE, open);
      document.body.classList.remove('m-shell');
    };
  }, []);

  // A new screen starts at the top and with no sheet left open.
  useEffect(() => {
    setMoreOpen(false);
    setNotesOpen(false);
  }, [pathname]);

  useScrollMemory();
  useLegacyDialogSwipe();
  useLegacyDialogBack();
  useKeyboardClass();

  return (
    <>
      <MobileTopBar unread={unread} onOpenNotifications={() => setNotesOpen(true)} />
      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} />
      <NotificationsSheet open={notesOpen} onClose={() => setNotesOpen(false)} onRead={refreshUnread} />
      <Toaster />
    </>
  );
};

const MobileShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isMobile = useIsMobile(768);
  const { isAuthenticated, user } = useAuth();
  const { pathname } = useLocation();
  const active = isMobile && isAuthenticated && !!user && !isPublicPath(pathname);
  return (
    <ChromeProvider>
      {active && <Frame />}
      {children}
    </ChromeProvider>
  );
};

export default MobileShell;
